import { useEffect, useMemo, useRef } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import { useTraces } from '../../../api/useUnwindDebug';
import { TraceQuery, UnwindTrace } from '../../../types/unwind-debug.types';
import { TraceRow } from './TraceRow';

export interface TracesViewProps {
  query: TraceQuery;
  liveEvents: UnwindTrace[];
  liveTail: boolean;
}

export function TracesView({ query, liveEvents, liveTail }: TracesViewProps) {
  const { data, isFetching, fetchNextPage, hasNextPage, refetch } = useTraces(query, { enabled: true });

  const pageItems = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data]
  );

  // when liveTail is on, we prepend liveEvents (already newest-first in the
  // hook) and de-dupe by _id against historical pages
  const items = useMemo(() => {
    if (!liveTail) return pageItems;
    const seen = new Set<string>();
    const out: UnwindTrace[] = [];
    for (const ev of liveEvents) {
      if (!ev._id || seen.has(ev._id)) continue;
      seen.add(ev._id);
      out.push(ev);
    }
    for (const ev of pageItems) {
      if (!ev._id || seen.has(ev._id)) continue;
      seen.add(ev._id);
      out.push(ev);
    }
    return out;
  }, [liveTail, liveEvents, pageItems]);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-refresh every 5s when not in live-tail mode
  useEffect(() => {
    if (liveTail) return;
    const t = setInterval(() => refetch(), 5_000);
    return () => clearInterval(t);
  }, [liveTail, refetch]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: 'rgba(0,0,0,0.2)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 1,
        }}
      >
        {items.length === 0 && !isFetching && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'grey.500' }}>
            <Typography>No trace events for this filter.</Typography>
          </Box>
        )}
        {items.map((t) => (
          <TraceRow key={t._id} trace={t} />
        ))}
        {hasNextPage && !liveTail && (
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Button size="small" onClick={() => fetchNextPage()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={14} /> : 'Load more'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
